// YourStuffScreen.tsx — "Your stuff": the user's own material in one place.
// Segments:
//   • Reflections — every reflection prompt they've reached (main programme,
//     journal beats + reflections, not Aha), grouped by Act in programme order;
//     tapping reopens it to sit with again on paper. (Default first tab.)
//   • Writing — their in-app reflection entries (only when in-app journaling is
//     on; otherwise hidden behind the IN_APP_JOURNALING flag, Reflections shows).
//   • Saved — everything they've hearted, filterable by type, searchable by title.
//   • Meditations — the guided-meditation library.
import React from 'react';
import { Screen } from '@/components/Screen';
import { Display, Eyebrow, Body, Meta, Pressable, Card } from '@/components/ui';
import { Icon, type IconName, BrandMark } from '@/components/icons';
import { FavouriteButton } from '@/components/FavouriteButton';
import { coverFor } from '@/components/FeedItemCard';
import { useProgress } from '@/hooks/useProgress';
import { useAuth } from '@/hooks/useAuth';
import { IN_APP_JOURNALING } from '@/config/features';
import { AHA_MOMENTS } from '@/config/uiFlags';
import { MEDITATIONS, type Meditation } from '@/data/meditations';
import {
  savedEntries,
  toggleSaved as libToggleSaved,
  categoryKicker as libKicker,
  tagLabel as libTagLabel,
  LIBRARY_EVENT,
  type Article as LibraryArticle,
} from '@/services/library';
import type { SavedReflection } from '@/services/progress';
import type { Programme, Step, Module } from '@/types';
import type { DisplayTitle } from '@/types/programme';
import { CARE_SECTION } from '@/data/care';
import { findStep, moduleOfStep, moduleName } from '@/services/contentProvider';
import { REAL_PROGRAMME } from '@/data/realProgramme';

const WRITING_INITIAL = 8; // how many recent reflections to show before "See all"

// Remembers the active "Your stuff" tab across remounts within a session, so that
// opening an item and closing it returns to the same tab rather than the default.
let lastSeg: Seg | null = null;

const KIND: Record<string, { icon: IconName; label: string }> = {
  video: { icon: 'film', label: 'Video' },
  article: { icon: 'book', label: 'Reading' },
  reflection: { icon: 'reflections', label: 'Reflection' },
  exercise: { icon: 'leaf', label: 'Exercise' },
  audio: { icon: 'audio', label: 'Audio' },
  mixed: { icon: 'sparkle', label: 'Lesson' },
};

// Clear, plural labels for the "Your saved items" filter pills.
const SAVED_LABEL: Record<string, string> = {
  video: 'Videos',
  article: 'Reading',
  exercise: 'Exercises',
  reflection: 'Reflections',
  audio: 'Audio',
  mixed: 'Lessons',
};
const SAVED_ORDER = ['video', 'article', 'exercise', 'reflection', 'audio', 'mixed'];

function stepMeta(step: Step): string {
  if (step.type === 'video' && step.duration) return `${Math.round(step.duration / 60)} min`;
  if (step.type === 'article' && step.read_minutes) return `${step.read_minutes} min read`;
  if (step.type === 'reflection') return '2 min';
  return '';
}

function titleText(t: DisplayTitle): string {
  return [t.pre, t.em, t.post ?? ''].join(' ');
}

function formatDay(iso: string): string {
  try {
    const d = new Date(iso);
    if (d.toDateString() === new Date().toDateString()) return 'Today';
    return d.toLocaleDateString('en-GB', { day: 'numeric', month: 'short' });
  } catch {
    return '';
  }
}

function monthKey(iso: string): string {
  const d = new Date(iso);
  return `${d.getFullYear()}-${String(d.getMonth()).padStart(2, '0')}`;
}

function monthLabel(iso: string): string {
  try {
    return new Date(iso).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });
  } catch {
    return 'Earlier';
  }
}

type Seg = 'reflections' | 'writing' | 'saved' | 'meditations';

// ── small UI atoms ───────────────────────────────────────────────────────────
// The first tab is "Reflections" (the prompts you've reached) by default, or
// "Writing" (your in-app entries) when in-app journaling is on.
function Segmented({ seg, onSeg, firstTab }: { seg: Seg; onSeg: (s: Seg) => void; firstTab: { id: Seg; label: string } }) {
  const opt = (id: Seg, label: string) => {
    const on = seg === id;
    return (
      <button
        type="button"
        onClick={() => onSeg(id)}
        aria-pressed={on}
        style={{
          flex: 1,
          padding: '9px 5px',
          borderRadius: 11,
          border: 'none',
          cursor: 'pointer',
          background: on ? 'var(--card-top)' : 'transparent',
          boxShadow: on ? 'var(--edge), var(--sh1)' : 'none',
          color: on ? 'var(--amber-deep)' : 'var(--ink-soft)',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 11.5,
          fontWeight: on ? 700 : 600,
          whiteSpace: 'nowrap',
        }}
      >
        {label}
      </button>
    );
  };
  return (
    <div style={{ display: 'flex', gap: 3, padding: 4, borderRadius: 13, background: 'var(--paper)', border: '1px solid var(--hair)', marginTop: 16 }}>
      {opt(firstTab.id, firstTab.label)}
      {opt('saved', 'Saved')}
      {opt('meditations', 'Meditations')}
    </div>
  );
}

// A meditation video row — poster thumbnail, title, running time, play affordance.
function MeditationRow({ m, locked, onOpen }: { m: Meditation; locked: boolean; onOpen: () => void }) {
  return (
    <Card pad={14} onClick={locked ? undefined : onOpen}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
        <div style={{ position: 'relative', width: 54, height: 54, borderRadius: 12, overflow: 'hidden', flexShrink: 0, background: 'var(--paper)' }}>
          <img src={m.poster_url} alt="" aria-hidden="true" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block', filter: locked ? 'grayscale(0.4) brightness(0.85)' : 'none' }} />
          <span aria-hidden="true" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(20,14,8,0.18)' }}>
            <span style={{ width: 26, height: 26, borderRadius: 999, background: 'rgba(255,250,240,0.94)', color: 'var(--green-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
              <Icon name={locked ? 'lock' : 'play'} size={13} />
            </span>
          </span>
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 3 }}>
            Meditation{m.minutes != null ? ` · ${m.minutes} min` : ''}
          </div>
          <Display parts={{ pre: m.title, em: '' }} size={16} lh={1.2} />
        </div>
      </div>
    </Card>
  );
}

function SearchField({ value, onChange, placeholder }: { value: string; onChange: (v: string) => void; placeholder: string }) {
  return (
    <div style={{ position: 'relative', marginTop: 12 }}>
      <span style={{ position: 'absolute', left: 13, top: '50%', transform: 'translateY(-50%)', color: 'var(--ink-soft)', opacity: 0.6, pointerEvents: 'none', display: 'flex' }}>
        <Icon name="search" size={16} />
      </span>
      <input
        value={value}
        onChange={(e) => onChange(e.target.value)}
        placeholder={placeholder}
        aria-label={placeholder}
        style={{
          width: '100%',
          boxSizing: 'border-box',
          padding: '11px 14px 11px 38px',
          borderRadius: 12,
          border: '1px solid var(--hair)',
          background: 'var(--card-top)',
          fontFamily: 'Inter, system-ui, sans-serif',
          fontSize: 14,
          color: 'var(--ink)',
          WebkitUserSelect: 'text',
          userSelect: 'text',
        }}
      />
    </div>
  );
}

function Chip({ active, onClick, icon, children }: { active: boolean; onClick: () => void; icon?: IconName; children: React.ReactNode }) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      style={{
        padding: '7px 13px',
        borderRadius: 999,
        border: active ? '1.5px solid var(--amber-deep)' : '1px solid var(--hair)',
        background: active ? 'rgba(200,134,26,0.10)' : 'var(--card-top)',
        color: active ? 'var(--amber-deep)' : 'var(--ink-soft)',
        cursor: 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 5,
        whiteSpace: 'nowrap',
        fontFamily: 'Inter, system-ui, sans-serif',
        fontSize: 12.5,
        fontWeight: 600,
      }}
    >
      {icon && <Icon name={icon} size={13} />}
      {children}
    </button>
  );
}

function MonthHeader({ label }: { label: string }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 10, margin: '20px 2px 11px' }}>
      <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>
        {label}
      </span>
      <span style={{ flex: 1, height: 1, background: 'var(--hair)' }} />
    </div>
  );
}

// A collapsible Act header for the Reflections tab — tap to fold/unfold that Act's
// reached prompts. Chevron points down when open, right when collapsed.
function CollapsibleHeader({ label, count, open, onToggle }: { label: string; count: number; open: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      onClick={onToggle}
      aria-expanded={open}
      style={{
        display: 'flex', alignItems: 'center', gap: 9, width: '100%',
        background: 'none', border: 'none', cursor: 'pointer',
        margin: '20px 0 11px', padding: '2px 2px', textAlign: 'left',
      }}
    >
      <span style={{ display: 'inline-flex', color: 'var(--ink-soft)', transition: 'transform .15s ease', transform: open ? 'none' : 'rotate(-90deg)' }}>
        <Icon name="chevDown" size={15} />
      </span>
      <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, fontWeight: 700, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-soft)' }}>
        {label}
      </span>
      <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, fontWeight: 600, color: 'var(--ink-soft)', opacity: 0.7 }}>
        {count}
      </span>
      <span style={{ flex: 1, height: 1, background: 'var(--hair)' }} />
    </button>
  );
}

function WritingRow({ r, programme, onOpen }: { r: SavedReflection; programme: Programme; onOpen: () => void }) {
  // aha moments render in gold so they stand apart as highlights
  if (AHA_MOMENTS && r.aha) {
    return (
      <Pressable onClick={onOpen} ariaLabel="Open aha moment">
        <div
          style={{
            position: 'relative',
            borderRadius: 'var(--r-card)',
            overflow: 'hidden',
            padding: '13px 15px',
            background: 'linear-gradient(160deg, #8a5e22, #5c3f15)',
            boxShadow: 'inset 0 1px 0 rgba(246,212,154,0.18), 0 1px 2px rgba(74,52,24,0.18), 0 16px 34px -20px rgba(60,40,10,0.5)',
          }}
        >
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: 6 }}>
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.12em', textTransform: 'uppercase', color: '#F0CE8E' }}>
              <Icon name="sparkle" size={13} /> Aha moment
            </span>
            <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: 'rgba(246,224,190,0.6)', whiteSpace: 'nowrap', flexShrink: 0 }}>
              {formatDay(r.savedAt)}
            </span>
          </div>
          <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 15.5, lineHeight: 1.5, color: '#FBF1DE', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
            {r.body}
          </div>
        </div>
      </Pressable>
    );
  }
  // Identify where this writing happened: the lesson title (or its chapter), so
  // the entry is recognisable by context rather than by a slice of its text.
  const isCare = r.module === CARE_SECTION;
  const step = isCare ? null : findStep(programme, r.stepId);
  const mod = step ? moduleOfStep(programme, step.id) : null;
  const eyebrow = isCare ? 'Calm Your Body' : mod ? moduleName(mod) : r.module || 'Journaling';
  const lessonTitle = !isCare && step ? titleText(step.title) : '';
  return (
    <Card well pad={'13px 14px'} onClick={onOpen}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 10, marginBottom: lessonTitle ? 4 : 5 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: isCare ? 'var(--green-deep)' : 'var(--amber-deep)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {isCare && <Icon name="leaf" size={12} />}
          {eyebrow}
        </span>
        <span style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 11, color: 'var(--ink-soft)', opacity: 0.75, whiteSpace: 'nowrap', flexShrink: 0 }}>
          {formatDay(r.savedAt)}
        </span>
      </div>
      {lessonTitle && (
        <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 15.5, fontWeight: 500, lineHeight: 1.22, color: 'var(--ink)', marginBottom: 5, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          {lessonTitle}
        </div>
      )}
      <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 13, lineHeight: 1.5, color: 'var(--ink-soft)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {r.body}
      </div>
    </Card>
  );
}

// A reflection you've reached — its prompt, by chapter. Tapping reopens it (to
// re-read and sit with again in your own journal). Read-and-return, not stored.
function ReflectionLibraryRow({ step, module, onOpen }: { step: Step; module: Module; onOpen: () => void }) {
  const prompt = (step.reflection_prompt ?? '').split('\n\n')[0] || titleText(step.title);
  return (
    <Card well pad={'13px 14px'} onClick={onOpen}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 6 }}>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--amber-deep)', whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
          <Icon name="reflections" size={12} /> {moduleName(module)}
        </span>
        <span style={{ color: 'var(--ink-soft)', opacity: 0.4, flexShrink: 0 }}>
          <Icon name="chevRight" size={15} />
        </span>
      </div>
      <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontStyle: 'italic', fontWeight: 300, fontSize: 15.5, lineHeight: 1.5, color: 'var(--amber-deep)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {prompt}
      </div>
    </Card>
  );
}

// Every completed reflection in the MAIN programme (journal beats + reflections,
// not Aha captures), grouped by Act in programme order ("as they arrive").
function reflectionLibrary(completed: string[]): { courseId: string; label: string; items: { step: Step; module: Module }[] }[] {
  const done = new Set(completed);
  const groups: { courseId: string; label: string; items: { step: Step; module: Module }[] }[] = [];
  const courses = [...(REAL_PROGRAMME.courses ?? [])].sort((a, b) => a.order - b.order);
  courses.forEach((course, ci) => {
    const items: { step: Step; module: Module }[] = [];
    for (const moduleId of course.moduleIds) {
      const mod = REAL_PROGRAMME.modules.find((m) => m.id === moduleId);
      if (!mod) continue;
      for (const step of mod.steps) {
        if (step.type === 'reflection' && !step.aha && done.has(step.id)) items.push({ step, module: mod });
      }
    }
    if (items.length) groups.push({ courseId: course.id, label: `Act ${ci + 1} · ${course.title}`, items });
  });
  return groups;
}

function SavedRow({ step, programme, onOpenStep }: { step: Step; programme: Programme; onOpenStep: (s: Step) => void }) {
  const module = moduleOfStep(programme, step.id);
  const kind = KIND[step.type] ?? KIND.mixed;
  const isVideo = step.type === 'video';
  return (
    <Card pad={14} onClick={() => onOpenStep(step)}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
        <div style={{ position: 'relative', width: 54, height: 54, borderRadius: 12, overflow: 'hidden', flexShrink: 0, background: 'var(--paper)' }}>
          <img src={coverFor(step)} alt="" aria-hidden="true" loading="lazy" decoding="async" style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }} />
          {isVideo && (
            <span aria-hidden="true" style={{ position: 'absolute', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'rgba(20,14,8,0.18)' }}>
              <span style={{ width: 26, height: 26, borderRadius: 999, background: 'rgba(255,250,240,0.94)', color: 'var(--amber-deep)', display: 'flex', alignItems: 'center', justifyContent: 'center', boxShadow: '0 2px 8px rgba(0,0,0,0.3)' }}>
                <Icon name="play" size={13} />
              </span>
            </span>
          )}
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, fontWeight: 600, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'var(--ink-soft)', marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {kind.label}
            {stepMeta(step) ? ` · ${stepMeta(step)}` : ''}
            {module ? ` · ${moduleName(module)}` : ''}
          </div>
          <Display parts={step.title} size={16} lh={1.2} />
        </div>
        <FavouriteButton stepId={step.id} />
      </div>
    </Card>
  );
}

// A saved Library article (a "beat"). Distinct store from programme favourites,
// but it lives in the same Saved viewer. The bookmark unsaves it; the screen
// re-renders via the LIBRARY_EVENT listener so the row drops out.
function LibrarySavedRow({ article, onOpen }: { article: LibraryArticle; onOpen: () => void }) {
  const kicker = libKicker(article);
  return (
    <Card pad={14} onClick={onOpen}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 13 }}>
        <div style={{ width: 54, height: 54, borderRadius: 12, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', background: 'var(--paper)', color: kicker }}>
          <Icon name="book" size={20} />
        </div>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontFamily: 'Inter, system-ui, sans-serif', fontSize: 10, fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: kicker, marginBottom: 3, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {libTagLabel(article.primaryShelf)}
            {article.duration ? ` · ${article.duration}` : ''}
          </div>
          <Display parts={{ pre: article.title, em: '' }} size={16} lh={1.2} />
        </div>
        <button
          type="button"
          onClick={(e) => { e.stopPropagation(); libToggleSaved(article.id); }}
          aria-label="Remove from saved"
          aria-pressed
          style={{ flexShrink: 0, width: 38, height: 38, borderRadius: 999, border: 'none', background: 'transparent', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--amber-deep)' }}
        >
          <Icon name="bookmark" size={18} />
        </button>
      </div>
    </Card>
  );
}

function EmptyState({ title, body }: { title: DisplayTitle; body: string }) {
  return (
    <div style={{ textAlign: 'center', padding: '44px 20px' }}>
      <BrandMark size={30} />
      <Display parts={title} size={20} style={{ marginTop: 14 }} />
      <Body dim size={13.5} style={{ marginTop: 8, maxWidth: 300, marginLeft: 'auto', marginRight: 'auto' }}>
        {body}
      </Body>
    </div>
  );
}

// ── screen ───────────────────────────────────────────────────────────────────
export function YourStuffScreen({
  programme,
  onOpenReflection,
  onOpenStep,
  onOpenMeditation,
  onOpenLibraryArticle,
}: {
  programme: Programme;
  onOpenReflection: (r: SavedReflection) => void;
  onOpenStep: (step: Step) => void;
  onOpenMeditation: (m: Meditation) => void;
  onOpenLibraryArticle: (id: string) => void;
}) {
  const progress = useProgress();
  const auth = useAuth();
  // Saved Library articles live in their own store; re-render when it changes
  // (e.g. unsaving a row here) so the Saved list stays in sync.
  const [, forceLib] = React.useReducer((x: number) => x + 1, 0);
  React.useEffect(() => {
    const h = () => forceLib();
    window.addEventListener(LIBRARY_EVENT, h);
    return () => window.removeEventListener(LIBRARY_EVENT, h);
  }, []);
  // Meditation videos are a members benefit. When activation isn't configured
  // (web/dev), nothing is gated, so they're open; otherwise only members can
  // play them (free users see them locked, as a taster of what's inside) —
  // consistent with the entitlement model.
  const isMember = auth.isMember;
  const inApp = IN_APP_JOURNALING && progress.state.inAppJournaling;
  // First tab: in-app entries ("Writing") when journaling is on, otherwise the
  // library of reached prompts ("Reflections").
  const firstTab = inApp ? { id: 'writing' as Seg, label: 'Writing' } : { id: 'reflections' as Seg, label: 'Reflections' };
  // Toolkit moved to the front page, so Your stuff now defaults to the first shelf
  // (Writing or Reflections). A tab picked in-session is remembered via lastSeg.
  const [seg, setSeg] = React.useState<Seg>(() => lastSeg ?? firstTab.id);
  const [query, setQuery] = React.useState('');
  const [wFilter, setWFilter] = React.useState<'journaling' | 'calm' | 'aha'>('journaling');
  const [sFilter, setSFilter] = React.useState<string>('all');
  const [showAllWriting, setShowAllWriting] = React.useState(false);
  // Reflections tab: which Acts are collapsed (folded). Empty = all expanded.
  const [collapsedActs, setCollapsedActs] = React.useState<Set<string>>(() => new Set());
  const toggleAct = (id: string) =>
    setCollapsedActs((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const q = query.trim().toLowerCase();
  // Keep the active segment valid as the first tab flips with the journaling flag.
  let effSeg: Seg = seg;
  if (seg === 'writing' && !inApp) effSeg = 'reflections';
  if (seg === 'reflections' && inApp) effSeg = 'writing';
  // Remember the active tab so it survives a remount (e.g. opening/closing a tool).
  React.useEffect(() => { lastSeg = effSeg; }, [effSeg]);

  // ── Reflections (the library of reached prompts, main programme) ──
  const reflectionGroups = React.useMemo(
    () => reflectionLibrary(progress.state.completedSteps),
    [progress.state.completedSteps],
  );

  // ── Writing ──
  const allWriting = React.useMemo(
    () =>
      Object.values(progress.state.reflections)
        .filter((r) => r.body.trim().length > 0)
        .sort((a, b) => (a.savedAt < b.savedAt ? 1 : -1)),
    [progress.state.reflections],
  );
  const journalingCount = allWriting.filter((r) => r.module !== CARE_SECTION).length;
  const calmCount = allWriting.filter((r) => r.module === CARE_SECTION).length;
  const ahaCount = allWriting.filter((r) => r.aha).length;

  const writing = allWriting.filter((r) => {
    const isCare = r.module === CARE_SECTION;
    if (wFilter === 'journaling' && isCare) return false;
    if (wFilter === 'calm' && !isCare) return false;
    if (wFilter === 'aha' && !r.aha) return false;
    if (q && !`${r.body} ${r.prompt} ${r.module}`.toLowerCase().includes(q)) return false;
    return true;
  });

  const browsingWriting = q === '';
  const writingShown = browsingWriting && !showAllWriting ? writing.slice(0, WRITING_INITIAL) : writing;

  const writingGroups: { key: string; label: string; items: SavedReflection[] }[] = [];
  for (const r of writingShown) {
    const key = monthKey(r.savedAt);
    let g = writingGroups.find((x) => x.key === key);
    if (!g) {
      g = { key, label: monthLabel(r.savedAt), items: [] };
      writingGroups.push(g);
    }
    g.items.push(r);
  }

  // ── Saved ── (programme favourites + saved Library articles, one viewer)
  const allSaved = [...progress.state.favourites]
    .reverse()
    .map((id) => findStep(programme, id))
    .filter((s): s is Step => !!s);
  // Saved Library articles count as "Reading" alongside programme article steps.
  const allSavedArticles = savedEntries();
  const savedTypeCounts: Record<string, number> = {};
  for (const s of allSaved) savedTypeCounts[s.type] = (savedTypeCounts[s.type] ?? 0) + 1;
  if (allSavedArticles.length) savedTypeCounts.article = (savedTypeCounts.article ?? 0) + allSavedArticles.length;
  const presentSavedTypes = SAVED_ORDER.filter((t) => (savedTypeCounts[t] ?? 0) > 0);
  const saved = allSaved.filter((s) => {
    if (sFilter !== 'all' && s.type !== sFilter) return false;
    if (q && !titleText(s.title).toLowerCase().includes(q)) return false;
    return true;
  });
  const savedArticles =
    sFilter === 'all' || sFilter === 'article'
      ? allSavedArticles.filter((a) => !q || a.title.toLowerCase().includes(q))
      : [];
  const savedIsEmpty = allSaved.length === 0 && allSavedArticles.length === 0;

  return (
    <Screen label="Your stuff">
      <div style={{ display: 'flex', alignItems: 'flex-end', justifyContent: 'space-between' }}>
        <div>
          <Eyebrow style={{ marginBottom: 9 }}>Kept by you</Eyebrow>
          <Display parts={{ pre: 'Your ', em: 'stuff', post: '.' }} size={25} />
        </div>
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, paddingBottom: 3 }}>
          <span style={{ color: 'var(--green-deep)' }}>
            <Icon name="shield" size={13} />
          </span>
          <Meta>Private</Meta>
        </span>
      </div>

      <Segmented seg={effSeg} firstTab={firstTab} onSeg={(s) => { setSeg(s); setQuery(''); }} />

      {/* ── REFLECTIONS (reached prompts, revisitable) ── */}
      {effSeg === 'reflections' && (
        reflectionGroups.length === 0 ? (
          <EmptyState
            title={{ pre: 'Your ', em: 'reflections', post: '.' }}
            body="As you move through the programme, every reflection you reach gathers here — come back to sit with any of them again, in your own time."
          />
        ) : (
          <>
            <Body dim size={12.5} style={{ marginTop: 14 }}>
              Every reflection you’ve reached. Tap any to read it again and take it to your own journal.
            </Body>
            {reflectionGroups.map((g) => {
              const open = !collapsedActs.has(g.courseId);
              return (
                <div key={g.courseId}>
                  <CollapsibleHeader label={g.label} count={g.items.length} open={open} onToggle={() => toggleAct(g.courseId)} />
                  {open && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                      {g.items.map(({ step, module }) => (
                        <ReflectionLibraryRow key={step.id} step={step} module={module} onOpen={() => onOpenStep(step)} />
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </>
        )
      )}

      {/* ── WRITING ── */}
      {effSeg === 'writing' && (
        !inApp && allWriting.length === 0 ? (
          <EmptyState
            title={{ pre: 'Your own ', em: 'journal', post: '.' }}
            body="You're writing your reflections in your own journal — the most private way, and the way we recommend. Nothing is kept here."
          />
        ) : allWriting.length === 0 ? (
          <EmptyState
            title={{ pre: 'Nothing kept ', em: 'yet', post: '.' }}
            body="When a lesson invites you to reflect, your words are saved here — private, and yours to revisit or change."
          />
        ) : (
          <>
            {!inApp && (
              <Body dim size={12.5} style={{ marginTop: 14 }}>
                You're journaling off-app now; these are earlier in-app entries. You can manage this in Settings.
              </Body>
            )}
            <SearchField value={query} onChange={setQuery} placeholder="Search your reflections…" />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              <Chip active={wFilter === 'journaling'} onClick={() => { setWFilter('journaling'); setShowAllWriting(false); }} icon="pencil">
                Journaling {journalingCount}
              </Chip>
              {AHA_MOMENTS && ahaCount > 0 && (
                <Chip active={wFilter === 'aha'} onClick={() => { setWFilter('aha'); setShowAllWriting(false); }} icon="sparkle">
                  Aha moments {ahaCount}
                </Chip>
              )}
              <Chip active={wFilter === 'calm'} onClick={() => { setWFilter('calm'); setShowAllWriting(false); }} icon="leaf">
                Calm Your Body {calmCount}
              </Chip>
            </div>

            {wFilter === 'aha' && (
              <Body dim size={13} style={{ marginTop: 14, fontStyle: 'italic' }}>
                The moments you marked as meaningful. Look how far you have come.
              </Body>
            )}

            {writing.length === 0 ? (
              <Body dim size={13.5} style={{ textAlign: 'center', padding: '32px 12px' }}>
                No reflections match that. Try a different word or filter.
              </Body>
            ) : (
              <>
                {writingGroups.map((g) => (
                  <div key={g.key}>
                    <MonthHeader label={g.label} />
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 9 }}>
                      {g.items.map((r) => (
                        <WritingRow key={r.stepId} r={r} programme={programme} onOpen={() => onOpenReflection(r)} />
                      ))}
                    </div>
                  </div>
                ))}
                {browsingWriting && !showAllWriting && writing.length > WRITING_INITIAL && (
                  <div style={{ display: 'flex', justifyContent: 'center', marginTop: 16 }}>
                    <Pressable
                      as="button"
                      onClick={() => setShowAllWriting(true)}
                      ariaLabel={`See all ${writing.length} reflections`}
                      style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: 6,
                        padding: '9px 16px',
                        borderRadius: 999,
                        border: '1px solid var(--hair)',
                        background: 'var(--card-top)',
                        color: 'var(--ink-soft)',
                        cursor: 'pointer',
                        fontFamily: 'Inter, system-ui, sans-serif',
                        fontSize: 13,
                        fontWeight: 600,
                      }}
                    >
                      See all {writing.length} <Icon name="chevDown" size={15} />
                    </Pressable>
                  </div>
                )}
              </>
            )}
          </>
        )
      )}

      {/* ── SAVED ── (programme favourites + saved Library articles) */}
      {effSeg === 'saved' && (
        savedIsEmpty ? (
          <EmptyState
            title={{ pre: 'Nothing saved ', em: 'yet', post: '.' }}
            body="Tap the heart on any lesson or reflection, or the bookmark on any reading, and it will be kept here, ready when you want to return to it."
          />
        ) : (
          <>
            <SearchField value={query} onChange={setQuery} placeholder="Search your saved items…" />
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 12 }}>
              <Chip active={sFilter === 'all'} onClick={() => setSFilter('all')}>All</Chip>
              {presentSavedTypes.map((t) => (
                <Chip key={t} active={sFilter === t} onClick={() => setSFilter(t)} icon={(KIND[t] ?? KIND.mixed).icon}>
                  {SAVED_LABEL[t] ?? (KIND[t] ?? KIND.mixed).label} {savedTypeCounts[t]}
                </Chip>
              ))}
            </div>

            {saved.length === 0 && savedArticles.length === 0 ? (
              <Body dim size={13.5} style={{ textAlign: 'center', padding: '32px 12px' }}>
                Nothing here matches that. Try a different word or filter.
              </Body>
            ) : (
              <div style={{ marginTop: 16, display: 'flex', flexDirection: 'column', gap: 11 }}>
                {saved.map((step) => (
                  <SavedRow key={step.id} step={step} programme={programme} onOpenStep={onOpenStep} />
                ))}
                {savedArticles.map((a) => (
                  <LibrarySavedRow key={a.id} article={a} onOpen={() => onOpenLibraryArticle(a.id)} />
                ))}
              </div>
            )}
          </>
        )
      )}

      {/* ── MEDITATIONS ── */}
      {effSeg === 'meditations' && (
        <>
          {!isMember && (
            <Card pad={'16px 16px'} style={{ marginTop: 16, background: 'linear-gradient(160deg, rgba(29,158,117,0.10), rgba(29,158,117,0.03))', border: '1px solid rgba(29,158,117,0.28)' }}>
              <div style={{ display: 'flex', alignItems: 'flex-start', gap: 11 }}>
                <span style={{ width: 34, height: 34, borderRadius: 999, background: 'var(--green-deep)', color: '#FBF1DE', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                  <Icon name="lock" size={16} />
                </span>
                <div style={{ flex: 1 }}>
                  <div style={{ fontFamily: 'Fraunces, Georgia, serif', fontSize: 16.5, fontWeight: 500, color: 'var(--ink)' }}>
                    A members’ collection
                  </div>
                  <Body dim size={13} style={{ marginTop: 4 }}>
                    The full library of guided meditations unlocks when you join the programme. You can preview the titles below.
                  </Body>
                </div>
              </div>
            </Card>
          )}
          <Body dim size={12.5} style={{ marginTop: isMember ? 16 : 14 }}>
            {MEDITATIONS.length} guided meditations — calming spaces, loving-kindness, grounding and more. Return to any of them whenever you need.
          </Body>
          <div style={{ marginTop: 14, display: 'flex', flexDirection: 'column', gap: 11 }}>
            {MEDITATIONS.map((m) => (
              <MeditationRow key={m.id} m={m} locked={!isMember} onOpen={() => onOpenMeditation(m)} />
            ))}
          </div>
        </>
      )}

      {/* ── TOOLKIT ── */}
    </Screen>
  );
}
